import { onBeforeUnmount, ref, type Ref } from 'vue';

export interface ClickAwayPanelOptions {
  /** A mousedown inside any of these keeps the panel open. A null ref (a closed v-if panel,
   *  say) is simply skipped. */
  anchors: Ref<HTMLElement | null>[];
  /** Fires synchronously when the panel opens, before the DOM mounts its contents. */
  onOpen?: () => void;
  /** Fires synchronously on every close path: toggle, Escape, outside mousedown, unmount. */
  onClose?: () => void;
}

// One source for "opens on click, closes on Escape or an outside mousedown": FilterPopover's
// behaviour, extracted so ElementWorldLink shares it. Kendo portals its dropdown lists to <body>,
// so a mousedown inside one of its containers is NOT an outside click — or a suggestion could
// never be picked with the mouse.
export function useClickAwayPanel(options: ClickAwayPanelOptions) {
  const open = ref(false);

  const isInsideKendoPopup = (el: HTMLElement): boolean =>
    !!el.closest('.k-animation-container, .k-child-animation-container, .k-list-container');
  const isAnchored = (el: HTMLElement): boolean =>
    options.anchors.some((anchor) => anchor.value?.contains(el) ?? false);

  const onDocMouseDown = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (isAnchored(target)) return;         // our own trigger/menu
    if (isInsideKendoPopup(target)) return; // Kendo portals to <body>
    close();
  };
  const onKeydown = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };

  const openPanel = () => {
    if (open.value) return;
    open.value = true;
    options.onOpen?.();
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeydown, true);
  };
  const close = () => {
    if (!open.value) return;
    open.value = false;
    document.removeEventListener('mousedown', onDocMouseDown, true);
    document.removeEventListener('keydown', onKeydown, true);
    options.onClose?.();
  };
  const toggle = () => { if (open.value) close(); else openPanel(); };

  onBeforeUnmount(close);

  return { open, toggle, close };
}
